/**
 * KALENDERN — den STORA öppna månadskalendern.
 *
 * ÄGAREN HAR VARIT TYDLIG: trycker man på kalenderremsan i underkanten av
 * startsidan ska det öppnas en stor månadskalender, INTE en lista med ordrar.
 * Det är den här skärmen. Prototypens `.storkal` (data-screen="tid", rad
 * 693-732) är facit för rutnätet: sju kolumner, en ruta per dag, datumet i
 * en cirkel och en liten bricka under med dagens timmar.
 *
 * RUTNÄTET ÄR NAVIGERING, INTE ETT TILLSTÅND. Beslut 04: attest och lås hänger
 * på VECKAN och ingenting annat. Månaden är bara ett sätt att bläddra sig till
 * en dag, så skärmen har ingen månadstotal och ingen låsning per månad — det
 * finns ingen sådan operation i kontraktet, där det heter 'week:lock', en
 * vecka i taget. RADEN UNDER RUTNÄTET är veckans: dess nummer, dess summa och
 * dess läge.
 *
 * INGA PRISER. Skärmen visar minuter, dagar och veckolägen. Beslut 16 gäller
 * även här, och oversikt.ts kan inte ens räkna fram ett timpris.
 */

import { useState } from 'react';
import { APPROVED_WEEK_STATES, asWorkDate } from '@delat/types';
import type { WeekState, WorkDate } from '@delat/types';
import {
  Bricka,
  Datumrad,
  Felruta,
  Knapp,
  Laddar,
  Listrad,
  Manadsrutnat,
  Snabbrad,
  Tomtext,
  Yta,
} from '../ui';
import {
  idag,
  veckaFor,
  visaDag,
  visaMil,
  visaMinuter,
  visaOren,
  visaVecka,
} from '../app/datum';
import { useInloggad } from '../app/session';
import { useNavigering, usePlats } from '../app/navigering';
import {
  FRANVARONAMN,
  VECKOLAGESNAMN,
  laddaOmOversikt,
  manadFor,
  minuterIVecka,
  plusManader,
  rutnatFor,
  useOversikt,
  veckolage,
  visaManad,
  visaManadsnamn,
} from './oversikt';
import type { Manad } from './oversikt';

/**
 * Dagen ur adressen. En felstavad eller ihopdiktad adress ska inte krascha
 * skärmen — asWorkDate kastar på allt som inte är 'YYYY-MM-DD', och då faller
 * vi tillbaka på dagens datum i Europe/Stockholm.
 */
const dagUrAdressen = (ratt: string | undefined, reserv: WorkDate): WorkDate => {
  if (ratt === undefined) return reserv;
  try {
    return asWorkDate(ratt);
  } catch {
    return reserv;
  }
};

/**
 * Brickans ton följer veckans läge. APPROVED_WEEK_STATES är kontraktets egen
 * mängd för "attesterat" (beslut 05) — 'reopened' står med FLIT inte i den, och
 * därför får en återöppnad vecka samma ton som en öppen.
 */
const veckoton = (lage: WeekState): 'klar' | 'pagaende' | 'neutral' =>
  APPROVED_WEEK_STATES.some((s) => s === lage)
    ? 'klar'
    : lage === 'submitted'
      ? 'pagaende'
      : 'neutral';

export const Kalender = () => {
  const { params } = usePlats();
  const { ga } = useNavigering();
  const { konto, firmaId } = useInloggad();
  const lage = useOversikt();

  const nu = idag();
  const start = dagUrAdressen(params['dag'], nu);

  const [vald, sattVald] = useState<WorkDate>(start);
  const [manad, sattManad] = useState<Manad>(() => manadFor(start));

  const o = lage.slag === 'klar' ? lage.oversikt : null;
  const rutnat = rutnatFor(o, manad, nu, true);

  const vecka = veckaFor(vald);
  const veckansMinuter = o === null ? 0 : minuterIVecka(o, vecka);
  const veckansLage = o === null ? 'open' : veckolage(o, vecka);

  const dagensRader = o?.tidPerDag.get(vald) ?? [];
  const dagensMinuter = o?.minuterPerDag.get(vald) ?? 0;
  const dagensFranvaro = o?.franvaroPerDag.get(vald);
  const dagensMeter = o?.meterPerDag.get(vald) ?? 0;
  const dagensUtlagg = o?.utlaggPerDag.get(vald);

  const foregaende = plusManader(manad, -1);
  const nasta = plusManader(manad, 1);

  return (
    <>
      {lage.slag === 'fel' && (
        <Felruta
          fel={lage.fel}
          atgard={
            <Knapp variant="piller-mini" onClick={() => laddaOmOversikt(firmaId, konto.id)}>
              Försök igen
            </Knapp>
          }
        />
      )}

      {/*
        STORA RUTNÄTET LIGGER DIREKT PÅ SKÄRMEN, inte i en <Yta>. Prototypen
        ställer .storkal rakt i <section data-screen="tid"> — och rutnätet har
        redan egen skugga, egen hårfin ram och egen radie i komponenter.css.
        Lagt i en <Yta> (som är ett .block med egen skugga och egen radie) blev
        det ett kort inuti ett kort, alltså "Ram runt allt, ram inuti ramen" ur
        appbokens "Inte så här"-panel. Månaden och veckan står i stället i en
        <Datumrad> ovanför, där de hör hemma.
      */}
      <Datumrad stark={visaManad(manad)}>
        {lage.slag === 'laddar' ? 'hämtar' : visaVecka(vecka)}
      </Datumrad>
      {lage.slag === 'laddar' ? (
        <Laddar rader={5} />
      ) : (
        <Manadsrutnat
          dagar={rutnat.dagar}
          tomma={rutnat.tomma}
          vald={vald}
          onValj={sattVald}
        />
      )}

      {/*
        MÅNADSBLÄDDRINGEN. Månadernas NAMN och inte två pilar: på en telefon i
        ett trapphus är "Augusti" tydligare än "<", och namnet säger också
        vilket håll man hamnar åt. Ingen ikon — `.qbtn` är ingen flexrad, så en
        ikon lägger sig OVANFÖR texten och gör knappen dubbelt så hög.
        En riktig <Manadsnavigering> hör hemma i src/ui/Kalender.tsx bredvid
        <Veckonavigering>; se LUCKOR i överlämningen.
      */}
      <Snabbrad>
        <Knapp variant="snabb" onClick={() => sattManad(foregaende)}>
          {visaManadsnamn(foregaende)}
        </Knapp>
        <Knapp variant="snabb" onClick={() => sattManad(nasta)}>
          {visaManadsnamn(nasta)}
        </Knapp>
      </Snabbrad>

      {/* VECKAN är periodenheten, inte månaden. Beslut 04. */}
      <Datumrad stark={visaDag(vald)}>
        {visaVecka(vecka)} · {visaMinuter(veckansMinuter)} <Bricka ton={veckoton(veckansLage)}>{VECKOLAGESNAMN[veckansLage]}</Bricka>
      </Datumrad>

      <Yta
        rubrik="Den här dagen"
        rakning={dagensMinuter > 0 ? visaMinuter(dagensMinuter) : undefined}
      >
        {dagensFranvaro !== undefined && (
          <Listrad titel={FRANVARONAMN[dagensFranvaro]} under="Frånvaro — ingen tid väntas" />
        )}
        {dagensRader.map((rad) => (
          <Listrad
            key={rad.id}
            titel={rad.rubrik}
            under={rad.kund ?? undefined}
            varde={visaMinuter(rad.minuter)}
          />
        ))}
        {dagensMeter > 0 && <Listrad titel="Mil" varde={visaMil(dagensMeter)} />}
        {dagensUtlagg !== undefined && (
          <Listrad
            titel={dagensUtlagg.antal === 1 ? 'Utlägg' : `Utlägg (${dagensUtlagg.antal} st)`}
            under="Ex moms"
            varde={visaOren(dagensUtlagg.oren)}
          />
        )}
        {dagensRader.length === 0 &&
          dagensFranvaro === undefined &&
          dagensMeter === 0 &&
          dagensUtlagg === undefined && (
            <Tomtext>
              {lage.slag === 'laddar' ? 'Hämtar dagen.' : 'Inget inlagt den här dagen.'}
            </Tomtext>
          )}
      </Yta>

      <Knapp onClick={() => ga('tid')}>Öppna tidrapporten</Knapp>

      <Tomtext>
        Grön dag är en hel dag rapporterad, gul en påbörjad, streckad ring en
        vardag som gått utan både tid och frånvaro. Veckan är det som lämnas in
        och attesteras — månaden är bara ett sätt att bläddra.
      </Tomtext>
    </>
  );
};
